import React from 'react';
import moment from 'moment';
import {
    AccessTime,
    ArrowForward,
    AutoGraph,
    CalendarMonth,
    Inventory2Outlined,
    PeopleOutline,
    PersonAddAlt1,
    Refresh,
    SpaOutlined,
    Today,
    Tune,
    WbSunnyOutlined,
} from '@mui/icons-material';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ContentViewComponent from '../../../Component/ContentViewComponent/ContentViewComponent';
import ApiHandler from '../../../Handler/ApiHandler';
import { getRoute, getRoutePathname } from '../../../Handler/RouteHandler';
import { dispatch } from '../../../App';
import { SnackbarOpen } from '../../../Reducer/SnackBarReducer/SnackBarSlice';

const APP_COLORS = {
    border: 'var(--customer_brown_light)',
    borderStrong: 'var(--customer_brown)',
    text: 'var(--greytext)',
    title: '#2f2430',
    cardBg: '#ffffff',
    heroStart: '#f3fbef',
    heroMid: '#ffffff',
    heroEnd: '#f5e3db',
    accentPrimary: '#8a5a4a',
    accentSecondary: '#315f5e',
    success: '#1f7a43',
};

const STATUS_UNKNOWN = 'unknown';
const STATUS_PENDING = 'pending';
const STATUS_CONFIRMED = 'confirmed';
const STATUS_DONE = 'done';
const STATUS_CANCELLED = 'cancelled';

function normalizeText(value) {
    return String(value || '')
        .toLowerCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

function getStatusKey(status) {
    const normalized = normalizeText(status);
    if (normalized.includes('annule') || normalized.includes('cancel')) return STATUS_CANCELLED;
    if (normalized.includes('termine') || normalized.includes('done')) return STATUS_DONE;
    if (normalized.includes('confirme') || normalized.includes('confirm')) return STATUS_CONFIRMED;
    if (normalized.includes('non confirme') || normalized.includes('pending')) return STATUS_PENDING;
    return STATUS_UNKNOWN;
}

function getStatusLabel(status) {
    const key = getStatusKey(status);
    if (key === STATUS_CANCELLED) return 'Annulé';
    if (key === STATUS_DONE) return 'Terminé';
    if (key === STATUS_CONFIRMED) return 'Confirmé';
    if (key === STATUS_PENDING) return 'Non confirmé';
    return String(status || 'Non défini');
}

function getStatusTone(status) {
    const key = getStatusKey(status);
    if (key === STATUS_CANCELLED) return { bg: '#fff0f0', border: '#f6cbcb', text: '#9a2f2f' };
    if (key === STATUS_DONE) return { bg: '#eaf9f0', border: '#cdeedb', text: '#216a41' };
    if (key === STATUS_CONFIRMED) return { bg: '#eaf4ff', border: '#caddf7', text: '#214f86' };
    if (key === STATUS_PENDING) return { bg: '#fff8ea', border: '#f4dfb5', text: '#8b6419' };
    return { bg: '#f3f4f6', border: '#e4e7ec', text: '#475467' };
}

function formatPrice(value) {
    return `${Number(value || 0).toFixed(2)} €`;
}

function formatAppointmentLabel(item) {
    const lastname = String(item?.customerLastname || '').trim();
    const firstname = String(item?.customerFirstname || '').trim();
    const name = `${lastname} ${firstname}`.trim();
    const prestation = String(item?.prestationLabel || '').trim();
    const time = item?.startAt ? moment(item.startAt).format('HH:mm') : '--:--';
    if (name && prestation) return `${time} · ${name} · ${prestation}`;
    if (name) return `${time} · ${name}`;
    if (prestation) return `${time} · ${prestation}`;
    return `${time} · Rendez-vous`;
}

function AdminDashboard() {
    const navigate = useNavigate();
    const authReducer = useSelector((state) => state.AuthReducer);
    const [loading, setLoading] = React.useState(true);
    const [refreshing, setRefreshing] = React.useState(false);
    const [lastUpdatedAt, setLastUpdatedAt] = React.useState(null);
    const [metrics, setMetrics] = React.useState({
        appointmentsToday: null,
        appointmentsWeek: null,
        revenueToday: null,
        revenueWeek: null,
        confirmedToday: null,
        cancelledToday: null,
        pendingToday: null,
        workMinutesToday: null,
        nextAppointmentLabel: null,
        latestPublicAppointmentsCount: null,
        customersTotal: null,
        prestationsTotal: null,
        groupsTotal: null,
        availabilitiesActive: null,
        availabilitiesTotal: null,
        usersTotal: null,
    });
    const [upcomingAppointments, setUpcomingAppointments] = React.useState([]);
    const [latestPublicAppointments, setLatestPublicAppointments] = React.useState([]);

    const roles = authReducer?.roles || [];
    const canCrm = roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_CRM');
    const canConfiguration = roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_CONFIGURATION');

    const quickActions = React.useMemo(() => {
        const actions = [
            {
                id: 'crm_appointment_list',
                title: 'Planning premium',
                description: 'Piloter la semaine et ouvrir la vue jour.',
                icon: <CalendarMonth />,
                tone: '#f3f1ff',
            },
            {
                id: 'crm_customer_list',
                title: 'Base clients',
                description: 'Rechercher, enrichir, qualifier les profils.',
                icon: <PeopleOutline />,
                tone: '#edf8ff',
            },
            {
                id: 'configuration_prestation_list',
                title: 'Catalogue soins',
                description: 'Ajuster prix, durées et contenus.',
                icon: <SpaOutlined />,
                tone: '#f1f9ef',
            },
            {
                id: 'configuration_availability_list',
                title: 'Disponibilités',
                description: 'Optimiser les plages d ouverture.',
                icon: <Tune />,
                tone: '#fff6ee',
            },
        ];

        return actions
            .map((action) => {
                const route = getRoute(action.id);
                if (!route?.path) {
                    return null;
                }

                return {
                    ...action,
                    path: getRoutePathname(action.id),
                };
            })
            .filter(Boolean);
    }, []);

    const loadDashboard = React.useCallback(
        async (isManualRefresh = false) => {
            if (isManualRefresh) {
                setRefreshing(true);
            }

            const getAsync = (options) =>
                new Promise((resolve) => {
                    ApiHandler.get(options, (response) => resolve(response));
                });

            const todayIso = moment().format('YYYY-MM-DD');
            const weekStartIso = moment().startOf('isoWeek').format('YYYY-MM-DD');
            const weekEndIso = moment().endOf('isoWeek').format('YYYY-MM-DD');

            const requests = [];
            if (canCrm) {
                requests.push(
                    getAsync({
                        route: 'crm_appointment_list',
                        data: {
                            limit: 5000,
                            offset: 0,
                            sortName: 'startAt',
                            sortDirection: 'asc',
                            dateFrom: todayIso,
                            dateTo: todayIso,
                        },
                    }),
                );
                requests.push(
                    getAsync({
                        route: 'crm_appointment_list',
                        data: {
                            limit: 5000,
                            offset: 0,
                            sortName: 'startAt',
                            sortDirection: 'asc',
                            dateFrom: weekStartIso,
                            dateTo: weekEndIso,
                        },
                    }),
                );
                requests.push(
                    getAsync({
                        route: 'crm_customer_list',
                        data: {
                            limit: 1,
                            offset: 0,
                        },
                    }),
                );
                requests.push(getAsync({ route: 'crm_appointment_notification_count' }));
                requests.push(
                    getAsync({
                        route: 'crm_appointment_notification_latest',
                        data: { limit: 5 },
                    }),
                );
            }

            if (canConfiguration) {
                requests.push(getAsync({ route: 'configuration_prestation_list' }));
                requests.push(getAsync({ route: 'configuration_prestation_group_list' }));
                requests.push(getAsync({ route: 'configuration_availability_list' }));
            }

            const responses = await Promise.all(requests);
            let cursor = 0;
            const nextMetrics = {
                appointmentsToday: null,
                appointmentsWeek: null,
                revenueToday: null,
                revenueWeek: null,
                confirmedToday: null,
                cancelledToday: null,
                pendingToday: null,
                workMinutesToday: null,
                nextAppointmentLabel: null,
                latestPublicAppointmentsCount: null,
                customersTotal: null,
                prestationsTotal: null,
                groupsTotal: null,
                availabilitiesActive: null,
                availabilitiesTotal: null,
            };
            const hasErrors = [];
            let nextUpcomingAppointments = [];
            let nextLatestPublicAppointments = [];

            if (canCrm) {
                const todayResponse = responses[cursor++];
                const weekResponse = responses[cursor++];
                const customerResponse = responses[cursor++];
                const publicNotificationCountResponse = responses[cursor++];
                const publicNotificationLatestResponse = responses[cursor++];

                if (todayResponse?.status === 200) {
                    const rows = todayResponse.data?.data || [];
                    let revenue = 0;
                    let workMinutes = 0;
                    let pending = 0;
                    let confirmed = 0;
                    let cancelled = 0;

                    rows.forEach((row) => {
                        const statusKey = getStatusKey(row.status);
                        if (statusKey === STATUS_PENDING) pending += 1;
                        if (statusKey === STATUS_CONFIRMED) confirmed += 1;
                        if (statusKey === STATUS_CANCELLED) cancelled += 1;

                        if (statusKey !== STATUS_CANCELLED) {
                            revenue += Number(row?.effectivePrice ?? row?.prestationPrice ?? 0);
                            workMinutes += Number(row?.effectivePlanningDuration ?? row?.effectiveDuration ?? 0);
                        }
                    });

                    const now = moment();
                    const sortedRows = [...rows].sort((a, b) => moment(a.startAt).valueOf() - moment(b.startAt).valueOf());
                    const nextAppointment = sortedRows.find(
                        (item) =>
                            moment(item.startAt).isAfter(now) &&
                            getStatusKey(item.status) !== STATUS_CANCELLED,
                    );

                    nextMetrics.appointmentsToday = Number(todayResponse.data?.count ?? rows.length ?? 0);
                    nextMetrics.revenueToday = revenue;
                    nextMetrics.confirmedToday = confirmed;
                    nextMetrics.cancelledToday = cancelled;
                    nextMetrics.pendingToday = pending;
                    nextMetrics.workMinutesToday = workMinutes;
                    nextMetrics.nextAppointmentLabel = nextAppointment
                        ? formatAppointmentLabel(nextAppointment)
                        : "Aucun rendez-vous à venir aujourd'hui";
                } else {
                    hasErrors.push('Rendez-vous du jour');
                }

                if (weekResponse?.status === 200) {
                    const rows = weekResponse.data?.data || [];
                    let weekRevenue = 0;
                    rows.forEach((row) => {
                        if (getStatusKey(row.status) !== STATUS_CANCELLED) {
                            weekRevenue += Number(row?.effectivePrice ?? row?.prestationPrice ?? 0);
                        }
                    });

                    const now = moment();
                    const sortedRows = [...rows].sort((a, b) => moment(a.startAt).valueOf() - moment(b.startAt).valueOf());
                    nextUpcomingAppointments = sortedRows
                        .filter(
                            (item) =>
                                moment(item.startAt).isAfter(now) &&
                                getStatusKey(item.status) !== STATUS_CANCELLED,
                        )
                        .slice(0, 5);

                    nextMetrics.appointmentsWeek = Number(weekResponse.data?.count ?? rows.length ?? 0);
                    nextMetrics.revenueWeek = weekRevenue;
                } else {
                    hasErrors.push('Rendez-vous semaine');
                }

                if (customerResponse?.status === 200) {
                    nextMetrics.customersTotal = Number(customerResponse.data?.count ?? 0);
                } else {
                    hasErrors.push('Clients');
                }

                if (publicNotificationCountResponse?.status === 200) {
                    nextMetrics.latestPublicAppointmentsCount = Number(publicNotificationCountResponse.data?.count ?? 0);
                } else {
                    hasErrors.push('Demandes web');
                }

                if (publicNotificationLatestResponse?.status === 200) {
                    nextLatestPublicAppointments = publicNotificationLatestResponse.data?.appointments || [];
                } else {
                    hasErrors.push('Dernières demandes');
                }
            }

            if (canConfiguration) {
                const prestationResponse = responses[cursor++];
                const groupResponse = responses[cursor++];
                const availabilityResponse = responses[cursor++];

                if (prestationResponse?.status === 200) {
                    nextMetrics.prestationsTotal = (prestationResponse.data || []).length;
                } else {
                    hasErrors.push('Prestations');
                }

                if (groupResponse?.status === 200) {
                    nextMetrics.groupsTotal = (groupResponse.data || []).length;
                } else {
                    hasErrors.push('Groupes');
                }

                if (availabilityResponse?.status === 200) {
                    const rows = availabilityResponse.data || [];
                    nextMetrics.availabilitiesTotal = rows.length;
                    nextMetrics.availabilitiesActive = rows.filter((item) => !!item.isActive).length;
                } else {
                    hasErrors.push('Disponibilités');
                }
            }

            setMetrics(nextMetrics);
            setUpcomingAppointments(nextUpcomingAppointments);
            setLatestPublicAppointments(nextLatestPublicAppointments);
            setLastUpdatedAt(moment());
            setLoading(false);
            setRefreshing(false);

            if (hasErrors.length > 0) {
                dispatch(
                    SnackbarOpen({
                        text: `Certaines KPI n'ont pas pu être chargées (${hasErrors.join(', ')}).`,
                        variant: 'warning',
                    }),
                );
            }
        },
        [canConfiguration, canCrm],
    );

    React.useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const kpiCards = [
        {
            id: 'appointmentsToday',
            label: "Rendez-vous aujourd'hui",
            value: metrics.appointmentsToday,
            icon: <Today sx={{ color: '#643b2f' }} />,
            helper: `${metrics.pendingToday ?? '--'} à confirmer`,
            gradient: 'linear-gradient(120deg, #fff5f0 0%, #ffffff 80%)',
        },
        {
            id: 'revenueToday',
            label: "CA du jour",
            value: metrics.revenueToday === null ? null : formatPrice(metrics.revenueToday),
            icon: <AutoGraph sx={{ color: '#17613d' }} />,
            helper: metrics.revenueWeek === null ? '--' : `Semaine: ${formatPrice(metrics.revenueWeek)}`,
            gradient: 'linear-gradient(120deg, #eefaf2 0%, #ffffff 80%)',
        },
        {
            id: 'appointmentsWeek',
            label: 'Rendez-vous semaine',
            value: metrics.appointmentsWeek,
            icon: <WbSunnyOutlined sx={{ color: '#3b4f66' }} />,
            helper: `Confirmés: ${metrics.confirmedToday ?? '--'}`,
            gradient: 'linear-gradient(120deg, #eff5ff 0%, #ffffff 80%)',
        },
    ];

    const dailyTarget = 10;
    const appointmentFill = React.useMemo(() => {
        if (!canCrm || metrics.appointmentsToday === null) return 0;
        return Math.min(100, Math.round((metrics.appointmentsToday / dailyTarget) * 100));
    }, [canCrm, metrics.appointmentsToday]);

    const confirmationRate = React.useMemo(() => {
        const total = Number(metrics.appointmentsToday || 0);
        if (total < 1) return 0;
        return Math.round((Number(metrics.confirmedToday || 0) / total) * 100);
    }, [metrics.appointmentsToday, metrics.confirmedToday]);

    const cancellationRate = React.useMemo(() => {
        const total = Number(metrics.appointmentsToday || 0);
        if (total < 1) return 0;
        return Math.round((Number(metrics.cancelledToday || 0) / total) * 100);
    }, [metrics.appointmentsToday, metrics.cancelledToday]);

    return (
        <ContentViewComponent
            loading={loading}
            breadcrumbs={{
                title: 'Accueil',
                context: 'Tableau de bord',
                description: '',
                links: [],
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '20px',
                    border: `1px solid ${APP_COLORS.border}`,
                    p: { xs: 1.2, md: 1.8 },
                    background: `linear-gradient(125deg, ${APP_COLORS.heroStart} 0%, ${APP_COLORS.heroMid} 52%, ${APP_COLORS.heroEnd} 100%)`,
                    boxShadow: '0 18px 42px rgba(89, 53, 37, 0.10)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        right: { xs: '-90px', md: '-40px' },
                        top: { xs: '-90px', md: '-70px' },
                        width: { xs: 180, md: 260 },
                        height: { xs: 180, md: 260 },
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(160, 101, 101, 0.24), rgba(160, 101, 101, 0))',
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: { xs: '-70px', md: '-40px' },
                        bottom: { xs: '-100px', md: '-90px' },
                        width: { xs: 170, md: 230 },
                        height: { xs: 170, md: 230 },
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(65, 135, 127, 0.20), rgba(65, 135, 127, 0))',
                    },
                }}
            >
                <Stack
                    direction={{ xs: 'column', xl: 'row' }}
                    spacing={1.6}
                    justifyContent="space-between"
                    sx={{ position: 'relative', zIndex: 2 }}
                >
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: APP_COLORS.text, textTransform: 'uppercase' }}>
                            Vision exécutive
                        </Typography>
                        <Typography sx={{ fontSize: { xs: 25, md: 33 }, fontWeight: 800, color: APP_COLORS.title, lineHeight: 1.05, mt: 0.35 }}>
                            Bonjour {authReducer.firstname || 'équipe'}, votre tableau de bord est prêt.
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: APP_COLORS.text, mt: 0.55 }}>
                            {moment().format('dddd DD MMMM YYYY')}
                            {lastUpdatedAt ? ` · synchronisé à ${lastUpdatedAt.format('HH:mm:ss')}` : ''}
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8} sx={{ mt: 1.1, flexWrap: 'wrap', gap: 0.8 }}>
                            {canCrm && (
                                <Chip
                                    icon={<PersonAddAlt1 />}
                                    label={`Demandes web: ${metrics.latestPublicAppointmentsCount ?? '--'}`}
                                    sx={{
                                        borderRadius: '9px',
                                        fontWeight: 700,
                                        backgroundColor: '#fff',
                                        border: '1px solid #e7d6cf',
                                    }}
                                />
                            )}
                            {canCrm && (
                                <Chip
                                    icon={<Inventory2Outlined />}
                                    label={`Confirmés: ${metrics.confirmedToday ?? '--'}`}
                                    sx={{
                                        borderRadius: '9px',
                                        fontWeight: 700,
                                        backgroundColor: '#eaf7ef',
                                        border: '1px solid #cce8d7',
                                    }}
                                />
                            )}
                            {canCrm && (
                                <Chip
                                    icon={<AccessTime />}
                                    label={`Annulés: ${metrics.cancelledToday ?? '--'}`}
                                    sx={{
                                        borderRadius: '9px',
                                        fontWeight: 700,
                                        backgroundColor: '#fff2f0',
                                        border: '1px solid #f1d4cf',
                                    }}
                                />
                            )}
                        </Stack>
                    </Box>

                    <Stack spacing={0.8} sx={{ minWidth: { xs: '100%', xl: 310 }, maxWidth: { xs: '100%', xl: 330 } }}>
                        <Box
                            sx={{
                                borderRadius: '16px',
                                border: '1px solid rgba(132, 94, 80, 0.26)',
                                backgroundColor: 'rgba(255, 255, 255, 0.82)',
                                backdropFilter: 'blur(8px)',
                                p: 1.1,
                                boxShadow: '0 10px 24px rgba(76, 47, 36, 0.09)',
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: APP_COLORS.text }}>
                                    Tension opérationnelle du jour
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Refresh />}
                                    onClick={() => loadDashboard(true)}
                                    disabled={refreshing}
                                    sx={{
                                        borderRadius: '9px',
                                        color: APP_COLORS.text,
                                        borderColor: APP_COLORS.borderStrong,
                                        backgroundColor: '#fff',
                                    }}
                                >
                                    {refreshing ? '...' : 'Refresh'}
                                </Button>
                            </Stack>

                            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mt: 1 }}>
                                <Box
                                    sx={{
                                        width: 88,
                                        height: 88,
                                        borderRadius: '50%',
                                        background: `conic-gradient(${APP_COLORS.accentPrimary} ${appointmentFill * 3.6}deg, #efdfd7 0deg)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.5)',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: '50%',
                                            backgroundColor: '#fff',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#402d26' }}>{appointmentFill}%</Typography>
                                        <Typography sx={{ fontSize: 9, color: APP_COLORS.text }}>objectif</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#1f2937' }}>
                                        {metrics.appointmentsToday ?? '--'} / {dailyTarget}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: APP_COLORS.text, mt: 0.2 }}>
                                        {metrics.nextAppointmentLabel || "Aucun rendez-vous à venir aujourd'hui"}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    </Stack>
                </Stack>
            </Box>

            <Box
                sx={{
                    mt: 1.2,
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        lg: 'repeat(3, minmax(0, 1fr))',
                        xl: 'repeat(3, minmax(0, 1fr))',
                    },
                }}
            >
                {kpiCards.map((card) => (
                    <Box
                        key={card.id}
                        sx={{
                            borderRadius: '14px',
                            border: `1px solid ${APP_COLORS.border}`,
                            background: card.gradient,
                            p: 1.1,
                            boxShadow: '0 6px 16px rgba(15, 23, 42, 0.05)',
                            transition: 'transform 160ms ease, box-shadow 160ms ease',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 10px 20px rgba(15, 23, 42, 0.11)',
                            },
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Typography sx={{ fontSize: 12, color: APP_COLORS.text, fontWeight: 700 }}>{card.label}</Typography>
                            {card.icon}
                        </Stack>
                        <Typography sx={{ fontSize: 24, color: '#111827', fontWeight: 800, mt: 0.45 }}>
                            {card.value === null ? '--' : card.value}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#6b7280', mt: 0.1 }}>{card.helper}</Typography>
                    </Box>
                ))}
            </Box>

            {canCrm && (
                <Box
                    sx={{
                        mt: 1.2,
                        display: 'grid',
                        gap: 1,
                        gridTemplateColumns: {
                            xs: '1fr',
                            xl: '1.7fr 1.3fr',
                        },
                    }}
                >
                    <Box
                        sx={{
                            borderRadius: '16px',
                            border: `1px solid ${APP_COLORS.border}`,
                            backgroundColor: '#fff',
                            p: 1.2,
                            boxShadow: '0 7px 16px rgba(15, 23, 42, 0.05)',
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#2b2430' }}>
                                Pilotage du jour
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: APP_COLORS.text }}>
                                Objectif: {dailyTarget} rendez-vous
                            </Typography>
                        </Stack>
                        <Box
                            sx={{
                                borderRadius: '12px',
                                border: '1px solid #ece7e4',
                                background: 'linear-gradient(180deg, #fff 0%, #fcfbfa 100%)',
                                p: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 0.8,
                                    gridTemplateColumns: {
                                        xs: 'repeat(2, minmax(0, 1fr))',
                                        md: 'repeat(4, minmax(0, 1fr))',
                                    },
                                }}
                            >
                                <Box sx={{ border: '1px solid #e6edf8', borderRadius: '10px', p: 0.8, backgroundColor: '#f5f9ff' }}>
                                    <Typography sx={{ fontSize: 11, color: '#4f6480', fontWeight: 700 }}>Confirmés</Typography>
                                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1f4e88' }}>
                                        {metrics.confirmedToday ?? '--'}
                                    </Typography>
                                </Box>
                                <Box sx={{ border: '1px solid #f1e4bc', borderRadius: '10px', p: 0.8, backgroundColor: '#fff9eb' }}>
                                    <Typography sx={{ fontSize: 11, color: '#826327', fontWeight: 700 }}>À confirmer</Typography>
                                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#8d6515' }}>
                                        {metrics.pendingToday ?? '--'}
                                    </Typography>
                                </Box>
                                <Box sx={{ border: '1px solid #f3d2d2', borderRadius: '10px', p: 0.8, backgroundColor: '#fff5f5' }}>
                                    <Typography sx={{ fontSize: 11, color: '#8a3232', fontWeight: 700 }}>Annulés</Typography>
                                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#9a2f2f' }}>
                                        {metrics.cancelledToday ?? '--'}
                                    </Typography>
                                </Box>
                                <Box sx={{ border: '1px solid #d5ece8', borderRadius: '10px', p: 0.8, backgroundColor: '#f2fbf9' }}>
                                    <Typography sx={{ fontSize: 11, color: '#2a6f67', fontWeight: 700 }}>CA semaine</Typography>
                                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#245c56' }}>
                                        {metrics.revenueWeek === null ? '--' : formatPrice(metrics.revenueWeek)}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ mt: 1 }}>
                                <Typography sx={{ fontSize: 11, color: APP_COLORS.text, mb: 0.35 }}>
                                    Taux de confirmation: {confirmationRate}%
                                </Typography>
                                <Box sx={{ height: 8, borderRadius: 6, backgroundColor: '#e7edf8', overflow: 'hidden' }}>
                                    <Box
                                        sx={{
                                            height: '100%',
                                            width: `${confirmationRate}%`,
                                            background: 'linear-gradient(120deg, #2b5e9f 0%, #2a8d8a 100%)',
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ mt: 0.7 }}>
                                <Typography sx={{ fontSize: 11, color: APP_COLORS.text, mb: 0.35 }}>
                                    Taux d'annulation: {cancellationRate}%
                                </Typography>
                                <Box sx={{ height: 8, borderRadius: 6, backgroundColor: '#f4e4e4', overflow: 'hidden' }}>
                                    <Box
                                        sx={{
                                            height: '100%',
                                            width: `${cancellationRate}%`,
                                            background: 'linear-gradient(120deg, #cc6f5f 0%, #ab3f3f 100%)',
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ mt: 0.9, borderTop: '1px dashed #eadfd9', pt: 0.8 }}>
                                <Typography sx={{ fontSize: 11, color: APP_COLORS.text, fontWeight: 700 }}>
                                    Prochain créneau
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: '#2f333b', mt: 0.15 }}>
                                    {metrics.nextAppointmentLabel || "Aucun rendez-vous à venir aujourd'hui"}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            borderRadius: '16px',
                            border: `1px solid ${APP_COLORS.border}`,
                            backgroundColor: '#fff',
                            p: 1.2,
                            boxShadow: '0 7px 16px rgba(15, 23, 42, 0.05)',
                        }}
                    >
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#2b2430', mb: 0.8 }}>
                            Prochains rendez-vous
                        </Typography>
                        <Stack spacing={0.7}>
                            {upcomingAppointments.length < 1 && (
                                <Typography sx={{ fontSize: 12, color: APP_COLORS.text }}>
                                    Aucun rendez-vous futur cette semaine.
                                </Typography>
                            )}
                            {upcomingAppointments.map((item) => {
                                const tone = getStatusTone(item.status);
                                return (
                                    <Box
                                        key={`upcoming_${item.id}`}
                                        sx={{
                                            borderRadius: '10px',
                                            border: '1px solid #ece7e4',
                                            p: 0.8,
                                            backgroundColor: '#fff',
                                        }}
                                    >
                                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1f2937' }}>
                                                    {moment(item.startAt).format('ddd DD/MM · HH:mm')}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: APP_COLORS.text, mt: 0.1 }}>
                                                    {`${item.customerLastname || ''} ${item.customerFirstname || ''}`.trim() || 'Client'}
                                                    {item.prestationLabel ? ` · ${item.prestationLabel}` : ''}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={getStatusLabel(item.status)}
                                                size="small"
                                                sx={{
                                                    fontWeight: 700,
                                                    borderRadius: '8px',
                                                    border: `1px solid ${tone.border}`,
                                                    backgroundColor: tone.bg,
                                                    color: tone.text,
                                                }}
                                            />
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                </Box>
            )}

            <Box
                sx={{
                    mt: 1.2,
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: {
                        xs: '1fr',
                        xl: canCrm ? '1.35fr 1.65fr' : '1fr',
                    },
                }}
            >
                {canCrm && (
                    <Box
                        sx={{
                            borderRadius: '16px',
                            border: `1px solid ${APP_COLORS.border}`,
                            backgroundColor: '#fff',
                            p: 1.2,
                            boxShadow: '0 7px 16px rgba(15, 23, 42, 0.05)',
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.9 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#2b2430' }}>
                                Demandes web récentes
                            </Typography>
                            <Button
                                size="small"
                                variant="text"
                                endIcon={<ArrowForward />}
                                onClick={() => navigate(getRoutePathname('crm_appointment_list'))}
                                sx={{ fontSize: 11, fontWeight: 700 }}
                            >
                                Voir planning
                            </Button>
                        </Stack>
                        <Stack spacing={0.7}>
                            {latestPublicAppointments.length < 1 && (
                                <Typography sx={{ fontSize: 12, color: APP_COLORS.text }}>
                                    Aucune nouvelle demande en attente.
                                </Typography>
                            )}
                            {latestPublicAppointments.map((item) => (
                                <Box
                                    key={`public_${item.id}`}
                                    sx={{
                                        borderRadius: '10px',
                                        border: '1px solid #ebe7e4',
                                        p: 0.8,
                                        background: 'linear-gradient(120deg, #fff 0%, #fcfaf9 100%)',
                                    }}
                                >
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1f2937' }}>
                                        {moment(item.startAt).format('ddd DD/MM · HH:mm')}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: APP_COLORS.text, mt: 0.15 }}>
                                        {`${item.customerLastname || ''} ${item.customerFirstname || ''}`.trim() || 'Client'}
                                        {item.prestationLabel ? ` · ${item.prestationLabel}` : ''}
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, color: '#8b8e96', mt: 0.15 }}>
                                        Reçu {item.createdAt ? moment(item.createdAt).fromNow() : 'récemment'}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}

                <Box
                    sx={{
                        borderRadius: '16px',
                        border: `1px solid ${APP_COLORS.border}`,
                        backgroundColor: '#fff',
                        p: 1.2,
                        boxShadow: '0 7px 16px rgba(15, 23, 42, 0.05)',
                    }}
                >
                    <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.8 }}>
                        <AccessTime sx={{ color: '#695a53' }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#3b2f2b' }}>
                            Accès rapides premium
                        </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: APP_COLORS.text, mb: 1 }}>
                        Lancez vos actions stratégiques en un clic.
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 0.9,
                            gridTemplateColumns: {
                                xs: '1fr',
                                md: 'repeat(2, minmax(0, 1fr))',
                            },
                        }}
                    >
                        {quickActions.map((action) => (
                            <Box
                                key={action.id}
                                sx={{
                                    borderRadius: '12px',
                                    border: `1px solid ${APP_COLORS.border}`,
                                    p: 0.9,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 1,
                                    backgroundColor: action.tone,
                                }}
                            >
                                <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0, flexGrow: 1 }}>
                                    <Box
                                        sx={{
                                            width: 34,
                                            height: 34,
                                            borderRadius: '10px',
                                            backgroundColor: '#fff',
                                            border: '1px solid #e9dbd4',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#59453f',
                                        }}
                                    >
                                        {action.icon}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#1f2937' }}>
                                            {action.title}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: APP_COLORS.text }}>
                                            {action.description}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => navigate(action.path)}
                                    sx={{ minWidth: 'auto', borderRadius: '9px', whiteSpace: 'nowrap' }}
                                >
                                    Ouvrir
                                </Button>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

        </ContentViewComponent>
    );
}

export default AdminDashboard;
